const SELECTORS = {
    container: '.wp-block-ssm-interactive-map__dropdown',
    list: '.wp-block-ssm-interactive-map__dropdown-list',
    toggleButton: '.wp-block-ssm-interactive-map__dropdown-toggle',
    label: '.wp-block-ssm-interactive-map__dropdown-label',
};

const ROOT_CONTAINER_SELECTOR = '.is-root-container';

// The Price slider is capped at $20M (see facetwp_index_row in ssm.php). When the
// upper handle sits at the cap, the label reads "$20,000,000+" to signal it
// includes every ranch at or above that price. Keep in sync with the PHP cap.
export const PRICE_SLIDER_MAX = 20000000;

const CLASSES = {
    open: 'is-open',
};

// Track all dropdown instances
const dropdownInstances = new Set();

export class Dropdown {
    constructor(toggle, block = null) {
        this.toggle = toggle;
        this.isOpen = false;
        this.block = block;
        this.originalLabel = '';

        this.handleToggleClick = this.handleToggleClick.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleEscapeKey = this.handleEscapeKey.bind(this);
        this.handleFacetSelection = this.handleFacetSelection.bind(this);
        this.handleFacetLoaded = this.handleFacetLoaded.bind(this);

        this.init();
    }

    init() {
        this.cacheDOM();
        if (!this.dropdown) return;

        // Store the original label text
        this.originalLabel = this.labelElement?.textContent?.trim() || '';

        this.bindEvents();
        this.setupAccessibility();

        // Register this instance
        dropdownInstances.add(this);

        setTimeout(() => {
            this.syncLabel();
        }, 100);
    }

    cacheDOM() {
        const isEditor = this.block?.closest(ROOT_CONTAINER_SELECTOR);
        const targetId = this.toggle.dataset.target;

        this.dropdown = !isEditor ? document.getElementById(targetId) : this.block?.querySelector(`#${targetId}`);
        this.container = this.toggle.closest(SELECTORS.container);
        this.labelElement = this.toggle.querySelector(SELECTORS.label);
    }

    bindEvents() {
        this.toggle.addEventListener('click', this.handleToggleClick);
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleEscapeKey);
        document.addEventListener('facetwp-loaded', this.handleFacetLoaded);

        // Listen for FacetWP facet clicks inside this dropdown
        this.dropdown.addEventListener('click', this.handleFacetSelection);
    }

    handleToggleClick(e) {
        e.stopPropagation();
        this.toggleDropdown();
    }

    handleDocumentClick(e) {
        if (this.dropdown?.contains(e.target)) return;
        if (this.toggle?.contains(e.target)) return;

        if (this.isOpen) {
            this.closeDropdown();
        }
    }

    handleEscapeKey(e) {
        if (e.key === 'Escape' && this.isOpen) {
            this.closeDropdown();
            this.toggle.focus();
        }
    }

    handleFacetSelection(e) {
        // Check if clicked element is a FacetWP facet choice
        const facetChoice = e.target.closest('.facetwp-checkbox, .facetwp-radio, .facetwp-link, [data-value]');

        if (facetChoice) {
            // Small delay to allow FacetWP to update the checked class
            setTimeout(() => {
                this.updateLabelFromDOM();
            }, 10);
        }
    }

    handleFacetLoaded() {
        this.syncLabel();
    }

    syncLabel() {
        const facet = this.dropdown?.querySelector('.facetwp-facet');
        if (!facet) return;

        if (facet.dataset.name === 'ranch_price') {
            this.updateToggleLabel();
        } else {
            this.updateLabelFromDOM();
        }
    }

    formatPrice(value) {
        const num = parseFloat(value);
        if (isNaN(num)) return value;

        return '$' + Math.round(num).toLocaleString('en-US');
    }

    updateToggleLabel() {
        if (!this.labelElement) return;

        const facet = this.dropdown.querySelector('.facetwp-facet');
        if (!facet) return;

        const facetName = facet.dataset.name;
        const isPrice = facetName === 'ranch_price';

        if (typeof FWP === 'undefined' || !FWP.facets) {
            this.labelElement.textContent = this.originalLabel;

            return;
        }

        const selectedValues = FWP.facets[facetName] || [];

        if (selectedValues.length === 0) {
            // No selection - restore original label
            this.labelElement.textContent = this.originalLabel;

            return;
        }

        if (isPrice) {
            this.labelElement.textContent = selectedValues
                .map((v, i) => {
                    const formatted = this.formatPrice(v);
                    const isUpperHandle = i === selectedValues.length - 1;

                    // Upper handle at the cap means "and above"
                    return isUpperHandle && parseFloat(v) >= PRICE_SLIDER_MAX
                        ? `${formatted}+`
                        : formatted;
                })
                .join(' — ');

            return;
        }

        // Get display values from the checked items
        const displayValues = selectedValues.map((value) => {
            const checkbox = this.dropdown.querySelector(`[data-value="${value}"] .facetwp-display-value`);

            return checkbox?.textContent?.trim() || value;
        });

        this.labelElement.textContent = displayValues.join(', ');
    }

    // Update label based on DOM state (checked class) - for immediate feedback before FWP refresh
    updateLabelFromDOM() {
        if (!this.labelElement) return;

        // Handle both checkboxes and radio buttons
        const checkedItems = this.dropdown.querySelectorAll('.facetwp-checkbox.checked, .facetwp-radio.checked');

        if (checkedItems.length === 0) {
            this.labelElement.textContent = this.originalLabel;

            return;
        }

        // Filter out "View All" or empty value selections (for radios)
        const validItems = Array.from(checkedItems).filter((item) => item.dataset.value !== '');

        if (validItems.length === 0) {
            this.labelElement.textContent = this.originalLabel;

            return;
        }

        const displayValues = validItems.map((item) => {
            const displayEl = item.querySelector('.facetwp-display-value');

            return displayEl?.textContent?.trim() || item.textContent?.trim() || item.dataset.value;
        });

        this.labelElement.textContent = displayValues.join(', ');
    }

    setupAccessibility() {
        this.toggle.setAttribute('aria-haspopup', 'listbox');
        this.toggle.setAttribute('aria-expanded', 'false');
        this.toggle.setAttribute('aria-controls', this.dropdown.id);
        this.dropdown.setAttribute('role', 'listbox');
    }

    toggleDropdown() {
        if (this.isOpen) {
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    }

    openDropdown() {
        // Close other dropdowns properly (updates their isOpen state)
        dropdownInstances.forEach((instance) => {
            if (instance !== this && instance.isOpen) {
                instance.closeDropdown();
            }
        });

        this.isOpen = true;
        this.dropdown.classList.remove('hidden');
        this.container.classList.add(CLASSES.open);
        this.toggle.setAttribute('aria-expanded', 'true');
    }

    closeDropdown() {
        this.isOpen = false;
        this.dropdown.classList.add('hidden');
        this.container.classList.remove(CLASSES.open);
        this.toggle.setAttribute('aria-expanded', 'false');
    }

    destroy() {
        if (this.toggle) {
            this.toggle.removeEventListener('click', this.handleToggleClick);
        }

        if (this.dropdown) {
            this.dropdown.removeEventListener('click', this.handleFacetSelection);
        }

        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleEscapeKey);
        document.removeEventListener('facetwp-loaded', this.handleFacetLoaded);

        // Unregister this instance
        dropdownInstances.delete(this);
    }
}
